import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import { centsToDollars, dashIfEmpty, formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { LoadMoreButton } from "./LoadMoreButton";
import { TableMessageRow } from "./TableMessageRow";

const COLUMNS = 9;

/** Order lifecycle: shipped or done = primary, cancelled or failed = destructive, waiting = secondary. */
function OrderStatusBadge({ status }: { status: string }) {
  const variant =
    status === "SHIPPED" || status === "SUCCESS"
      ? "default"
      : status === "CANCELLED" || status === "FAIL"
        ? "destructive"
        : status === "HOLD" || status === "UNAPPROVED"
          ? "secondary"
          : "outline";
  return <Badge variant={variant}>{status.toLowerCase().replace("_", " ")}</Badge>;
}

function snapshotName(snapshot: Record<string, unknown>, fallback: string): string {
  return typeof snapshot.name === "string" && snapshot.name ? snapshot.name : fallback;
}

/**
 * The user's fulfillment orders from `users.orders`, withdrawals marked; a row expands to its
 * lines because no order page exists yet. Fulfillment money is dollars.
 */
export function UserOrdersTab({ endUserId }: { endUserId: string }) {
  const trpc = useTRPC();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const query = useInfiniteQuery(
    trpc.users.orders.infiniteQueryOptions(
      { endUserId, limit: 25 },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Created</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead className="text-right">Lines</TableHead>
            <TableHead className="text-right">Cost</TableHead>
            <TableHead className="text-right">Shipping</TableHead>
            <TableHead>Tracking</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isLoading && <TableMessageRow colSpan={COLUMNS}>Loading…</TableMessageRow>}
          {query.error && (
            <TableMessageRow colSpan={COLUMNS} tone="error">
              {query.error.message}
            </TableMessageRow>
          )}
          {query.isSuccess && rows.length === 0 && (
            <TableMessageRow colSpan={COLUMNS}>
              No orders yet. Orders exist for live environments only.
            </TableMessageRow>
          )}
          {rows.map(({ order, lines, isWithdrawal }) => {
            const isOpen = expanded.has(order.id);
            return [
              <TableRow key={order.id}>
                <TableCell className="p-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Hide lines" : "Show lines"}
                    onClick={() => toggle(order.id)}
                  >
                    {isOpen ? <ChevronDown /> : <ChevronRight />}
                  </Button>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(order.createdAt)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{order.type.toLowerCase().replace("_", " ")}</Badge>
                </TableCell>
                <TableCell>
                  <OrderStatusBadge status={order.status} />
                </TableCell>
                <TableCell>
                  {isWithdrawal ? (
                    <Badge variant="secondary">withdrawal</Badge>
                  ) : (
                    <span className="text-muted-foreground">purchase</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">{lines.length}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {order.totalCost === null ? "—" : centsToDollars(order.totalCost)}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {order.shippingFee === 0 ? "—" : centsToDollars(order.shippingFee)}
                  {order.shippingFeeRefunded && (
                    <span className="ml-1 text-muted-foreground">refunded</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {dashIfEmpty(order.trackingNumber)}
                </TableCell>
              </TableRow>,
              isOpen && (
                <TableRow key={`${order.id}-lines`} className="bg-muted/40 hover:bg-muted/40">
                  <TableCell />
                  <TableCell colSpan={COLUMNS - 1} className="py-2">
                    {lines.length === 0 ? (
                      <span className="text-sm text-muted-foreground">No lines.</span>
                    ) : (
                      <ul className="grid gap-1 text-sm">
                        {lines.map((line) => (
                          <li key={line.id} className="flex flex-wrap items-baseline gap-x-3">
                            <span>{snapshotName(line.snapshot, line.itemId)}</span>
                            <span className="text-xs text-muted-foreground">
                              {line.itemSource.toLowerCase()}
                              {line.inventoryItemId ? ` · ${line.inventoryItemId}` : ""}
                            </span>
                            <span className="font-mono text-xs">
                              {centsToDollars(line.unitValue)}
                            </span>
                            <Badge variant="outline">{line.status.toLowerCase()}</Badge>
                          </li>
                        ))}
                      </ul>
                    )}
                  </TableCell>
                </TableRow>
              ),
            ];
          })}
        </TableBody>
      </Table>
      <LoadMoreButton
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </>
  );
}
